import { memo, useCallback, useState } from 'react'
import { MultiStrikeChartTypes, setMultiStrikeChartType } from '../../../reducer/multiStrikeChart.reducer'
import { useDispatch, useSelector } from 'react-redux'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { FormControl, Menu, MenuItem, Select, SelectChangeEvent, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { selectMultiStrikeChartConfigChartType, selectMultiStrikeChartConfigCumOi, selectMultiStrikeChartConfigShowChange } from '../../../selectors/chart'
import { selectMultiStrikeChartType, selectMultiStrikeCumOi, selectMultiStrikeShowChange } from '../../../selectors/multiStrikeChart'
import { useStyles } from './styles'
import { ExtraSmallButton } from '../../../components/custom'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../../components/adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import { isOiValueChartType, getMultiStrikeChartTypeMenuValue } from '../../../components/multiStikeChart/multistrikeChartTypeHelpers'
import { useWidgetSettingsMenuProps, useWidgetSettingsSelectMenuProps } from '../../../components/widgetDashboard/widgetSettingsMenuContext'

type Props = {
    viewKey: string
    isWidget: boolean
    compact?: boolean
    dropdown?: boolean
    fullWidth?: boolean
}

const CHART_TYPE_MENU_LABELS = {
    price: 'Price',
    iv: 'IV',
    oi: 'OI',
    oiChange: 'OI Chg',
} as const

type ChartTypeMenuValue = keyof typeof CHART_TYPE_MENU_LABELS

const getChartTypeDisplayName = (chartType: MultiStrikeChartTypes, cumOi: boolean, showChange: boolean): string => {
    return CHART_TYPE_MENU_LABELS[getMultiStrikeChartTypeMenuValue(chartType, cumOi, showChange)]
}

const getChartTypeDropdownLabel = (menuValue: ChartTypeMenuValue): string => {
    switch (menuValue) {
        case 'price':
            return 'Price'
        case 'iv':
            return 'Imp. Vol'
        case 'oi':
            return 'Open Int.'
        case 'oiChange':
            return 'OI Change'
    }
}

const MultiStrikeChartTypeMenu = ({ viewKey, isWidget, compact = false, dropdown = false, fullWidth = false }: Props) => {
    const dispatch = useDispatch()
    const { classes } = useStyles()
    const { user: { hasAccess } } = useUser()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
    const menuProps = useWidgetSettingsMenuProps('left')
    const selectMenuProps = useWidgetSettingsSelectMenuProps('left')
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const showChange = useSelector(isWidget ? selectMultiStrikeChartConfigShowChange(viewKey) : selectMultiStrikeShowChange)

    const menuValue = getMultiStrikeChartTypeMenuValue(chartType, cumOi, showChange)

    const applyChartType = useCallback(
        (value: string) => {
            const nextChartType =
                value === 'oi' && cumOi && !showChange
                    ? 'cumOi'
                    : (value as MultiStrikeChartTypes)

            if (isOiValueChartType(nextChartType) && !hasAccess(FeatureCategory.MULTI_STRIKE, FeatureSubCategory.OI_CHARTS)) {
                dispatch(setFeatureRestrictionDialog({
                    open: true,
                    feature: 'OI Charts',
                    featureRestrictionCode: 'MULTI_STRIKE_OI_CHARTS_MULTISTRIKE',
                    group: Group.MULTI_STRIKE
                }))
                return
            }

            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({
                    viewKey,
                    config: { chartType: nextChartType, cumOi: false, showChange: false },
                }))
            } else {
                dispatch(setMultiStrikeChartType(nextChartType))
            }
            AnalyticsManager.logEvent('Chart Type Changed', { type: nextChartType, source: 'multistrike' })
        },
        [dispatch, isWidget, viewKey, cumOi, showChange, hasAccess]
    )

    const handleChartTypeChange = useCallback(
        (_e: any, value: string) => {
            if (value) {
                applyChartType(value)
            }
        },
        [applyChartType]
    )

    const handleSelectChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            applyChartType(event.target.value)
        },
        [applyChartType]
    )

    if (dropdown) {
        return (
            <FormControl size="small" fullWidth={fullWidth} sx={fullWidth ? { width: '100%' } : undefined}>
                <Select
                    fullWidth={fullWidth}
                    value={menuValue}
                    onChange={handleSelectChange}
                    MenuProps={selectMenuProps}
                    renderValue={(value) => getChartTypeDropdownLabel(value as ChartTypeMenuValue)}
                    sx={{ fontSize: '13px', height: 32, ...(fullWidth ? { width: '100%' } : { minWidth: 120 }) }}
                >
                    <MenuItem value="price">Price</MenuItem>
                    <MenuItem value="iv">Imp. Vol</MenuItem>
                    <MenuItem value="oi">Open Int.</MenuItem>
                    <MenuItem value="oiChange">OI Change</MenuItem>
                </Select>
            </FormControl>
        )
    }

    return !compact ? (
        <ToggleButtonGroup
            id={'mulstrike-price-options'}
            className={classes.item}
            value={menuValue}
            exclusive
            size="small"
            onChange={handleChartTypeChange}
            aria-label="text alignment"
            sx={{
                textWrap: 'nowrap',
                height: '35px',
                '.MuiButtonBase-root': {
                    borderColor: (theme) => theme.palette.background.default
                },
                ml: isWidget ? '0 !important' : '8px !important'
            }}
        >
            <ToggleButton value="iv" aria-label="centered">
                Imp. Vol
            </ToggleButton>
            <ToggleButton value="oi" aria-label="right aligned">
                Open Int.
            </ToggleButton>
            <ToggleButton value="oiChange" aria-label="oi change">
                OI Chg
            </ToggleButton>
            <ToggleButton value="price" aria-label="left aligned">
                Price
            </ToggleButton>
        </ToggleButtonGroup>
    ) : (
        <>
            <ExtraSmallButton
                color="inherit"
                sx={{ margin: 0, padding: '0 4px', fontSize: '13px', minWidth: 24 }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                {getChartTypeDisplayName(chartType, cumOi, showChange)}
            </ExtraSmallButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={compact && !!anchorEl}
                onClose={() => setAnchorEl(undefined)}
                {...menuProps}
                MenuListProps={{
                    'aria-labelledby': 'basic-button'
                }}
            >
                <MenuItem selected={menuValue === 'iv'} onClick={(e) => handleChartTypeChange(e, 'iv')}>IV</MenuItem>
                <MenuItem selected={menuValue === 'oi'} onClick={(e) => handleChartTypeChange(e, 'oi')}>OI</MenuItem>
                <MenuItem selected={menuValue === 'oiChange'} onClick={(e) => handleChartTypeChange(e, 'oiChange')}>OI Chg</MenuItem>
                <MenuItem selected={menuValue === 'price'} onClick={(e) => handleChartTypeChange(e, 'price')}>Price</MenuItem>
            </Menu>
        </>
    )
}

export default memo(MultiStrikeChartTypeMenu)
